import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSyncedState } from "../demo/live/useSyncedState";
import { UserPlus, ShieldCheck, FileText, ChevronDown, ChevronRight, Check } from "lucide-react";
// Reused prod compliance logic + the REAL consent template DATA (pure display half).
import { selfConsentAge, SELF_CONSENT_AGE } from "@/lib/compliance/selfConsentAge";
import { ageInYears, classifyMinorConsent, buildConsentDocSet, AGE_OF_MAJORITY } from "@/lib/compliance/minorConsent";
import { useDemoState } from "../demo/DemoStateProvider";
import { DEMO_THERAPIST, DEFAULT_SETTINGS } from "../demo/mock-data";
import { track } from "../demo/analytics";
import { SampleConsentDoc } from "../demo/SampleConsentDoc";
import { cn } from "@/lib/utils";

const STATES = ["WA", "CA", "OR", "PA", "TX", "UT", "NY", "FL"];

type Step = "intake" | "consent" | "done";

/**
 * AddClientFlow (Scope A9) — runs the real onboarding workflow against stubs:
 * intake → state-aware consent path (reusing classifyMinorConsent + the real
 * FORM_CONTENT templates) → a COSMETIC mock-sign that stores nothing in any
 * backend. The pseudo-client lands in the in-memory store and a refresh resets it.
 * No e-signature service, no submission endpoint, no storage (Scope DO-NOT).
 */
export function AddClientFlow() {
  const { addClient } = useDemoState();
  const navigate = useNavigate();

  // Co-browsed so the guest watches the whole scripted onboarding — the wizard step
  // and every intake/sign field — not just the client appearing at the end. The
  // consent classification below is derived, so it re-adapts on the guest for free
  // as the presenter edits DOB/state. useSyncedState === useState in the static demo.
  const [step, setStep] = useSyncedState<Step>("acf.step", "intake");
  const [displayName, setDisplayName] = useSyncedState("acf.displayName", "Riley Sample");
  const [dob, setDob] = useSyncedState("acf.dob", "2010-05-04"); // ~16y — showcases the consent logic
  const [state, setState] = useSyncedState("acf.state", "WA");
  const [signName, setSignName] = useSyncedState("acf.signName", "");
  const [agreed, setAgreed] = useSyncedState("acf.agreed", false);

  // Live consent-path classification — recomputes as the prospect edits DOB/state,
  // so they can watch the state-aware logic adapt before continuing.
  const classification = useMemo(() => {
    const age = ageInYears(new Date(dob), new Date());
    const threshold = selfConsentAge(state); // the state's self-consent age (or 18 if none)
    const c = classifyMinorConsent({ ageYears: age, effectiveMinorConsentAge: threshold });
    // The split the prospect cares about: under the threshold → guardian signs;
    // at/over it (adult, or eligible minor) → the client signs for themselves.
    const bucket: "self" | "guardian" = c.consentPath === "parental" ? "guardian" : "self";
    return { ...c, age, threshold, bucket };
  }, [dob, state]);

  const docs = useMemo(
    () =>
      buildConsentDocSet({
        consentPath: classification.consentPath,
        stateForms: [
          { form_type: "standard_adult_consent", required_by_states: [state] },
          { form_type: "telehealth_consent", required_by_states: [state] },
        ],
      }),
    [classification.consentPath, state],
  );

  const pathLabel =
    classification.consentPath === "minor_self"
      ? "Minor self-consent"
      : classification.consentPath === "parental"
        ? "Parent / guardian consent"
        : "Adult — self consent";

  function handleSign() {
    addClient({
      id: `c-${Date.now()}`,
      displayName: displayName.trim() || "New Sample Client",
      dob,
      state,
      consentStatus: "signed",
      modality: "EMDR",
      savedPrefs: { ...DEFAULT_SETTINGS },
    });
    track("add_client_completed", { consentPath: classification.consentPath ?? "self" });
    setStep("done");
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <Stepper step={step} />

      {step === "intake" && (
        <section data-tour="intake-form" className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-6">
          <h1 className="font-heading text-2xl font-medium text-[var(--color-text-primary)]">Add a client</h1>
          <p className="mt-1 text-[14px] text-[var(--color-text-muted)]">
            Pre-filled with sample data. Change the date of birth or state and watch the consent path adapt.
          </p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Client label (pseudonymous)">
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls} />
            </Field>
            <Field label="State of care">
              <select value={state} onChange={(e) => setState(e.target.value)} className={inputCls}>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Date of birth">
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className={inputCls} />
            </Field>
          </div>

          <ConsentBucketCard
            bucket={classification.bucket}
            consentPath={classification.consentPath}
            age={classification.age}
            state={state}
            threshold={classification.threshold}
            pathLabel={pathLabel}
          />

          <StateAgeReference activeState={state} />

          <button
            data-tour="continue-consent"
            type="button"
            onClick={() => setStep("consent")}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            Continue to consent →
          </button>
        </section>
      )}

      {step === "consent" && (
        <section className="mt-6">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-6">
            <h1 className="font-heading text-2xl font-medium text-[var(--color-text-primary)]">
              Review &amp; sign — {pathLabel}
            </h1>
            <p className="mt-1 text-[14px] text-[var(--color-text-muted)]">
              These are the real Psy180 consent templates for a {classification.age.toFixed(0)}-year-old client in {state}.
              In the live app the client signs these from a tokenized link. Here they&apos;re read-only samples.
            </p>

            <div className="mt-5 space-y-3">
              {docs.map((d, i) => (
                <SampleConsentDoc
                  key={d.form_type + i}
                  formType={d.form_type}
                  states={Array.from(new Set([state, ...d.required_by_states]))}
                  clinicName={DEMO_THERAPIST.clinic}
                  defaultOpen={i === 0}
                />
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] p-4">
              <label className="flex items-start gap-3">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 accent-[var(--color-gold)]" />
                <span className="text-[13px] leading-relaxed text-[var(--color-text-muted)]">
                  I have reviewed the sample documents above. (This is a cosmetic demo step — nothing is submitted, stored, or legally binding.)
                </span>
              </label>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  value={signName}
                  onChange={(e) => setSignName(e.target.value)}
                  placeholder="Type a name to 'sign'"
                  className={cn(inputCls, "max-w-xs")}
                />
                <button
                  type="button"
                  disabled={!agreed || signName.trim().length < 2}
                  onClick={handleSign}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="h-4 w-4" /> Sign (demo)
                </button>
                <button type="button" onClick={() => setStep("intake")} className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] hover:text-[var(--color-gold-soft)]">
                  ← Back
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {step === "done" && (
        <section className="mt-6 rounded-2xl border border-[var(--color-border-gold)] bg-[var(--color-navy-mid)] p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[var(--color-border-gold)] text-[var(--color-gold-soft)]">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-medium text-[var(--color-text-primary)]">
            {displayName} added to your workspace
          </h1>
          <p className="mx-auto mt-2 max-w-md text-[14px] text-[var(--color-text-muted)]">
            In the live app, the consent links would now be emailed to the client (or guardian). Here, nothing was
            stored — refresh the page and this sample client disappears.
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            Back to dashboard →
          </button>
        </section>
      )}
    </main>
  );
}

const inputCls =
  "w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5 text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold)]";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">{label}</span>
      {children}
    </label>
  );
}

/**
 * Shows the consent SPLIT for the chosen state — under the self-consent age
 * (guardian signs) vs. at/over it (the client signs) — and which side this client
 * falls on. Threshold + classification come from the real prod compliance logic.
 */
function ConsentBucketCard({
  bucket, consentPath, age, state, threshold, pathLabel,
}: {
  bucket: "self" | "guardian";
  consentPath: "parental" | "minor_self" | "dual" | null;
  age: number;
  state: string;
  threshold: number;
  pathLabel: string;
}) {
  const noSelfConsent = threshold >= AGE_OF_MAJORITY;
  const implication =
    consentPath === "minor_self"
      ? `A minor at or above ${state}'s self-consent age can authorize their own outpatient therapy — and their records stay private from guardians except for safety.`
      : consentPath === "parental"
        ? `A minor under ${state}'s self-consent age needs a parent or legal guardian to authorize treatment.`
        : `An adult (18+) consents to their own treatment.`;

  return (
    <div data-tour="consent-split" className="mt-5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-4">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--color-text-ghost)]">
        Consent split · {state} self-consent age:{" "}
        <span className="text-[var(--color-gold-soft)]">{noSelfConsent ? "18 (no minor self-consent)" : threshold}</span>
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <BucketChip
          active={bucket === "guardian"}
          heading={`Under ${threshold}`}
          label="Guardian consent"
          sub="A parent / legal guardian signs."
        />
        <BucketChip
          active={bucket === "self"}
          heading={`${threshold} and over`}
          label="Self-consent"
          sub="The client signs for themselves."
        />
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
        This client is <strong className="text-[var(--color-text-primary)]">{age.toFixed(0)}</strong> in{" "}
        <strong className="text-[var(--color-text-primary)]">{state}</strong> →{" "}
        <strong className="text-[var(--color-gold-soft)]">{pathLabel}</strong>. {implication}
      </p>
    </div>
  );
}

function BucketChip({ active, heading, label, sub }: { active: boolean; heading: string; label: string; sub: string }) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5 transition-colors",
        active ? "border-[var(--color-gold)] bg-[var(--color-navy-light)]" : "border-[var(--color-border)] bg-[var(--color-navy-mid)] opacity-60",
      )}
    >
      <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{heading}</p>
      <p className={cn("font-heading text-[15px] font-medium", active ? "text-[var(--color-gold-soft)]" : "text-[var(--color-text-primary)]")}>
        {active ? "→ " : ""}{label}
      </p>
      <p className="text-[12px] text-[var(--color-text-muted)]">{sub}</p>
    </div>
  );
}

/** Reference: the states that grant minors an earlier self-consent age (the rest are 18). */
function StateAgeReference({ activeState }: { activeState: string }) {
  const [open, setOpen] = useState(true);
  const byAge = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const [st, age] of Object.entries(SELF_CONSENT_AGE)) {
      if (!m.has(age)) m.set(age, []);
      m.get(age)!.push(st);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([age, states]) => ({ age, states: states.sort() }));
  }, []);
  const activeHasSpecialAge = activeState in SELF_CONSENT_AGE;

  return (
    <div data-tour="state-ref" className="mt-3 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)]">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
          Self-consent age by state
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-[var(--color-text-ghost)]" /> : <ChevronRight className="h-4 w-4 text-[var(--color-text-ghost)]" />}
      </button>
      {open && (
        <div className="border-t border-[var(--color-border)] p-4">
          <p className="mb-3 text-[12px] leading-relaxed text-[var(--color-text-ghost)]">
            At or above the age below, a minor can self-consent to outpatient therapy. Below it, a guardian signs. Every
            other state defaults to <strong className="text-[var(--color-text-muted)]">18</strong> (guardian consent for all minors).
          </p>
          <ul className="space-y-1.5">
            {byAge.map(({ age, states }) => (
              <li key={age} className="flex items-baseline gap-3">
                <span className="w-10 shrink-0 font-heading text-[15px] font-medium text-[var(--color-gold-soft)]">{age}</span>
                <span className="flex flex-wrap gap-1.5">
                  {states.map((s) => (
                    <span
                      key={s}
                      className={cn(
                        "rounded border px-2 py-0.5 font-mono text-[11px]",
                        s === activeState ? "border-[var(--color-gold)] bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]",
                      )}
                    >
                      {s}
                    </span>
                  ))}
                </span>
              </li>
            ))}
            <li className="flex items-baseline gap-3 border-t border-[var(--color-border)] pt-2">
              <span className="w-10 shrink-0 font-heading text-[15px] font-medium text-[var(--color-text-muted)]">18</span>
              <span className={cn("font-mono text-[11px]", !activeHasSpecialAge ? "text-[var(--color-gold-soft)]" : "text-[var(--color-text-ghost)]")}>
                All other states{!activeHasSpecialAge ? ` (incl. ${activeState})` : ""} — guardian consent for all minors
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: "intake", label: "Intake", icon: <UserPlus className="h-3.5 w-3.5" /> },
    { key: "consent", label: "Consent", icon: <FileText className="h-3.5 w-3.5" /> },
    { key: "done", label: "Done", icon: <Check className="h-3.5 w-3.5" /> },
  ];
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2">
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide",
              i <= idx ? "border-[var(--color-border-gold)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-ghost)]",
            )}
          >
            {s.icon} {s.label}
          </span>
          {i < steps.length - 1 && <span className="text-[var(--color-text-ghost)]">→</span>}
        </li>
      ))}
    </ol>
  );
}

